import { useCallback } from 'react'
import { toggleUpvote, toggleDownvote } from '@/services/ideasService'

/**
 * Minimal shape every card-list item needs to opt into the shared vote-handler.
 * Each consumer page extends its own card type from this — `IdeaCardData`
 * already does in practice (id + votes + hasUpvoted + hasDownvoted).
 */
export type VotableCard = {
  id: string
  votes: number
  hasUpvoted?: boolean
  hasDownvoted?: boolean
}

/**
 * Optimistic up/down handlers shared by every IdeaCard consumer (validate
 * grid, dashboard tabs, home live-ideas, etc). Mirrors the backend's
 * mutual-exclusion exactly: clicking the opposite arrow flips both the
 * highlighted state AND the count by 2 (drop one + add one). On API
 * failure, the row rolls back to its pre-click snapshot.
 *
 * `votes` is the net score (upvotes - downvotes), so:
 *   - up while neutral: +1
 *   - up while down:    +2 (-1 from down side, +1 to up side)
 *   - up while up:      -1 (toggle off)
 *   - down: symmetric
 */
export function useCardVoteHandlers<T extends VotableCard>(
  ideas: T[],
  setIdeas: (updater: (prev: T[]) => T[]) => void,
) {
  const apply = useCallback(
    (id: string, fn: (i: T) => T) =>
      setIdeas((prev) => prev.map((i) => (i.id === id ? fn(i) : i))),
    [setIdeas],
  )

  const restore = useCallback(
    (id: string, snapshot: T) => apply(id, () => snapshot),
    [apply],
  )

  const handleUpvote = useCallback(
    (id: string) => {
      const before = ideas.find((i) => i.id === id)
      if (!before) return
      apply(id, (i) => {
        if (i.hasUpvoted) {
          return { ...i, hasUpvoted: false, votes: i.votes - 1 }
        }
        const flipBack = i.hasDownvoted ? 1 : 0
        return {
          ...i,
          hasUpvoted: true,
          hasDownvoted: false,
          votes: i.votes + 1 + flipBack,
        }
      })
      toggleUpvote(id).catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[useCardVoteHandlers] upvote rejected — reverting', {
          ideaId: id,
          error: err,
        })
        restore(id, before)
      })
    },
    [ideas, apply, restore],
  )

  const handleDownvote = useCallback(
    (id: string) => {
      const before = ideas.find((i) => i.id === id)
      if (!before) return
      apply(id, (i) => {
        if (i.hasDownvoted) {
          return { ...i, hasDownvoted: false, votes: i.votes + 1 }
        }
        const flipBack = i.hasUpvoted ? 1 : 0
        return {
          ...i,
          hasDownvoted: true,
          hasUpvoted: false,
          votes: i.votes - 1 - flipBack,
        }
      })
      toggleDownvote(id).catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[useCardVoteHandlers] downvote rejected — reverting', {
          ideaId: id,
          error: err,
        })
        restore(id, before)
      })
    },
    [ideas, apply, restore],
  )

  return { handleUpvote, handleDownvote }
}
